import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updateNodeData } from '../../../../store/workflow/workflowSlice';
import { typeLabelOf } from '../../nodeCatalog';
import { Section, inp } from './AnalyticsNodePanel';

/**
 * 분기 · 합류 · 사용자확인 · 메모 · 반복그룹/그룹 속성 — 집계·데이터비교와 같은 모양(2026-10-20).
 * 예전에는 RJSF 폼(schemas/index.ts 의 branch/merge/userConfirm/memo)이었고, 반복그룹은 화면이 없어
 * 「No schema defined for node type」 이 보였다. 제목·도움말 문구는 그 스키마의 번역 키를 그대로 쓴다.
 * 전용 패널이 없는 유형(옛 캔버스의 옛 유형 등)도 여기로 온다 — 이름만 고친다.
 */
export const CONTROL_PANEL_TYPES = new Set(['branch', 'merge', 'userConfirm', 'memo', 'repeatGroup', 'group']);

interface Props { node: any }

const Radio: React.FC<{ value: string; options: Array<[string, string]>; onChange: (v: string) => void }> = ({ value, options, onChange }) => (
  <div className="flex flex-col gap-0.5 text-xs text-gray-700">
    {options.map(([v, label]) => (
      <label key={v} className="flex items-center gap-1"><input type="radio" checked={value === v} onChange={() => onChange(v)} />{label}</label>
    ))}
  </div>
);

const ControlNodePanel: React.FC<Props> = ({ node }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const cfg = node?.data?.config || {};
  const type = String(node.type);
  const patch = useCallback((p: Record<string, any>) => {
    dispatch(updateNodeData({ id: node.id, data: { config: { ...cfg, ...p }, ...(p.label !== undefined ? { label: p.label } : {}) } } as any));
  }, [cfg, dispatch, node.id]);

  return (
    <div className="h-full bg-white p-4 overflow-auto">
      <div className="mb-3 pb-3 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">{t('property:ui.properties')}</h3>
        <div className="flex items-center gap-2"><span className="text-xs font-semibold text-gray-500">{t('property:an.nodeType')}</span>
          <input type="text" value={typeLabelOf(type)} readOnly className="text-xs text-gray-700 bg-gray-100 border-none p-1 rounded w-auto" style={{ minWidth: 80 }} /></div>
      </div>
      <div className="mb-3"><label className="block text-xs font-semibold text-gray-500 mb-1">{t('workflow:field.label')}</label>
        <input className="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={cfg.label ?? node.data?.label ?? ''}
          placeholder={t(type === 'memo' ? 'workflow:schema.memoTitlePlaceholder' : 'workflow:field.labelPlaceholder')}
          onChange={(e) => patch({ label: e.target.value })} /></div>

      {type === 'branch' && (
        <>
          <Section title={t('workflow:schema.condition')} hint={t('workflow:schema.conditionHelp')}>
            <textarea className={`${inp} w-full font-mono`} rows={4} value={cfg.condition || ''} placeholder={t('workflow:schema.conditionPlaceholder')}
              onChange={(e) => patch({ condition: e.target.value })} />
          </Section>
          <Section title={t('workflow:schema.defaultPath')} hint={t('workflow:schema.defaultPathHelp')}>
            <Radio value={cfg.defaultPath || 'TRUE'} options={[['TRUE', t('property:ctl.pathTrue')], ['FALSE', t('property:ctl.pathFalse')]]}
              onChange={(v) => patch({ defaultPath: v })} />
          </Section>
        </>
      )}

      {type === 'merge' && (
        <Section title={t('workflow:schema.joinType')} hint={t('workflow:schema.joinTypeHelp')}>
          <Radio value={cfg.joinType || 'ALL'} options={[['ALL', t('property:ctl.mergeAll')], ['ANY', t('property:ctl.mergeAny')]]}
            onChange={(v) => patch({ joinType: v })} />
        </Section>
      )}

      {type === 'userConfirm' && (
        <>
          <Section title={t('workflow:schema.message')} hint={t('workflow:schema.messageHelp')}>
            <textarea className={`${inp} w-full`} rows={3} value={cfg.message || ''} placeholder={t('workflow:schema.messagePlaceholder')}
              onChange={(e) => patch({ message: e.target.value })} />
          </Section>
          <Section title={t('workflow:schema.timeout')} hint={t('workflow:schema.timeoutHelp')}>
            <input className={`${inp} w-28`} type="number" min={1} value={cfg.timeoutSecs ?? 86400}
              onChange={(e) => patch({ timeoutSecs: Number(e.target.value) || 86400 })} />
            <label className="mt-1.5 flex items-center gap-1 text-xs text-gray-700">
              <input type="checkbox" checked={!!cfg.continueOnTimeout} onChange={(e) => patch({ continueOnTimeout: e.target.checked })} />
              {t('workflow:schema.continueOnTimeout')}</label>
            <div className="text-[0.6875rem] text-gray-400 mt-0.5">{t('workflow:schema.continueOnTimeoutHelp')}</div>
          </Section>
        </>
      )}

      {type === 'memo' && (
        <Section title={t('workflow:schema.memoText')}>
          <textarea className={`${inp} w-full`} rows={8} value={cfg.text || ''} placeholder={t('workflow:schema.memoTextPlaceholder')}
            onChange={(e) => patch({ text: e.target.value })} />
        </Section>
      )}

      {!CONTROL_PANEL_TYPES.has(type) && (
        <div className="text-[0.6875rem] text-gray-500">{t('property:ctl.noSettings')}</div>
      )}

      {(type === 'repeatGroup' || type === 'group') && (
        <div className="text-[0.6875rem] text-gray-500">{t(type === 'repeatGroup' ? 'property:ctl.repeatGroupHint' : 'property:ctl.groupHint')}</div>
      )}
    </div>
  );
};

export default ControlNodePanel;
